import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Select,
  Divider,
  Card,
  Typography,
  Space,
  Switch,
  message,
  Button,
  Tooltip,
} from 'antd';
import { useLazyQuery } from '@apollo/react-hooks';
import GlobalButton from '../../../shared/globalStyling/Button';
import { CheckOutlined, CloseOutlined, RiseOutlined, UndoOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import GOOGLE_ADS_ACCESSIBLE_CUSTOMERS from '../../../../core/GraphQl/Queries/GOOGLE_ADS_ACCESSIBLE_CUSTOMERS';
import GOOGLE_ANALYTICS_ACCESSIBLE_PROPERTIES from '../../../../core/GraphQl/Queries/GOOGLE_ANALYTICS_ACCESSIBLE_PROPERTIES';
const { Text } = Typography;

const LaunchPaidSearch = ({
  currentOrgId = '', //Current Org ID
  orgs = [], //Child Orgs to be displayed in the modal
  tags = [], //Tags Paid Search Commerce
  onSubmit = () => {}, //Function to be called when the modal is submitted
}) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false); //Modal Pop up
  const [selectedOrg, setSelectedOrg] = useState(null); //Currently Selected org to be launched by default use login org
  const [isRevenueByLocation, setIsRevenueByLocation] = useState(false); //Revenue by Location enabled
  const [isProcessWithGoogleAds, setIsProcessWithGoogleAds] = useState(false); //Process with Google Ads enabled. When enabled Google Analytics will be disabled
  const [isCommerceEnabled, setIsCommerceEnabled] = useState(false); //Commerce Enabled. When enabled Commerce Tag will be enabled
  const [googleAdsCustomers, setGoogleAdsCustomers] = useState([]);
  const [googleAnalyticsProperties, setGoogleAnalyticsProperties] = useState([]);
  const [fetchGoogleAdsCustomers, { loading: loadingGoogleAdsCustomers }] = useLazyQuery(
    GOOGLE_ADS_ACCESSIBLE_CUSTOMERS,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        if (data && data.googleAdsAccessibleCustomers && data.googleAdsAccessibleCustomers.customers) {
          setGoogleAdsCustomers(data.googleAdsAccessibleCustomers.customers);
        }
      },
    }
  );
  const [fetchGoogleAnalyticsProperties, { loading: loadingGoogleAnalyticsProperties }] = useLazyQuery(
    GOOGLE_ANALYTICS_ACCESSIBLE_PROPERTIES,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        if (data && data.googleAnalyticsAccessibleProperties) {
          var result = data.googleAnalyticsAccessibleProperties;
          var options = [];
          // Add GA4 properties
          if (result.properties) {
            result.properties.forEach(function(prop) {
              options.push({
                viewId: prop.propertyId,
                displayName: prop.displayName + ' (' + prop.propertyId + ')',
                type: 'GA4',
              });
            });
          }
          // Add UA views
          if (result.uaViews) {
            result.uaViews.forEach(function(view) {
              options.push({
                viewId: view.viewId,
                displayName: view.displayName,
                type: 'UA',
              });
            });
          }
          setGoogleAnalyticsProperties(options);
        }
      },
    }
  );

  //Set default values of the form
  const defaultSet = () => {
    const org = orgs.find((o) => o.id === currentOrgId);
    if (org && org.dataConfig) {
      const config = org.dataConfig;
      setSelectedOrg(org);
      form.setFieldsValue({
        organization: currentOrgId,
        googleAdsID: config.googleCustomerId ? config.googleCustomerId : null,
        googleAnalyticsID:
          config.googleAnalytics && config.googleAnalytics.viewId
            ? config.googleAnalytics.viewId
            : null,
        googleAnalyticsViewIdIsGA4:
          config.googleAnalytics && config.googleAnalytics.isGA4
            ? config.googleAnalytics.isGA4
            : null,
        bingCustomerID: config.bing && config.bing.customerId ? config.bing.customerId : null,
        bingAccountID: config.bing && config.bing.accountId ? org.dataConfig.bing.accountId : null,
      });
      form.validateFields();
    }
  };

  //Set default values of the form when currentOrgId changes
  useEffect(() => {
    if (currentOrgId) {
      const org = orgs.find((o) => o.id === currentOrgId);
      if (org && org.dataConfig) {
        const config = org.dataConfig;
        setSelectedOrg(org);
        form.setFieldsValue({
          organization: currentOrgId,
          googleAdsID: config.googleCustomerId ? config.googleCustomerId : null,
          googleAnalyticsID:
            config.googleAnalytics && config.googleAnalytics.viewId
              ? config.googleAnalytics.viewId
              : null,
          googleAnalyticsViewIdIsGA4:
            config.googleAnalytics && config.googleAnalytics.isGA4
              ? config.googleAnalytics.isGA4
              : null,
          bingCustomerID: config.bing && config.bing.customerId ? config.bing.customerId : null,
          bingAccountID:
            config.bing && config.bing.accountId ? org.dataConfig.bing.accountId : null,
        });
        form.validateFields();
      }
    }
  }, [currentOrgId, form, orgs]);

  //Event Handler for showing launch modal
  const showModal = () => {
    setVisible(true);
  };

  //Event Handler for Launching Paid Search
  const handleOk = async () => {
    // Logic for handling the launch action
    form.validateFields().then(async (values) => {
      message.loading('Launching Paid Search');
      try {
        await onSubmit(values);
        message.destroy();
        message.success('Paid Search Launched');
        form.resetFields();
        defaultSet();
        setVisible(false);
      } catch (error) {
        message.destroy();
        message.error(error.message || 'Failed to launch paid search campaign');
      }
    });
  };

  //Event Handler for Canceling Paid Search
  const handleCancel = () => {
    form.resetFields();
    defaultSet();
    setVisible(false);
  };
  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col className="ant-col-xs-100 ant-col-md-flex-auto">
          <GlobalButton type="primary" onClick={showModal}>
            Launch Paid Search
          </GlobalButton>
        </Col>
      </Row>

      <Modal
        title="Launch Paid Search"
        visible={visible}
        onOk={async () => {
          await handleOk();
        }}
        onCancel={handleCancel}
        okText="Launch"
        width={'80%'}
      >
        <Form form={form} layout="vertical" name={'launchPaidSearch'}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Form.Item
                label="Organization"
                name={'organization'}
                rules={[
                  () => ({
                    validator(_, value) {
                      const org = orgs.find((o) => o.id === value);
                      if (org) {
                        if (org.paidSearchSummary && org.paidSearchSummary.id) {
                          //If organization has already a paid search throw error
                          return Promise.reject(`Organization already has a paid search`);
                        }
                        return Promise.resolve();
                      }

                      return Promise.reject();
                    },
                  }),
                ]}
              >
                <Select
                  showSearch
                  filterOption={(input, option) => {
                    return option.label.toLowerCase().includes(input.toLowerCase());
                  }}
                  disabled={orgs.length === 0}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  options={orgs.map((org) => {
                    return { label: org.name, value: org.id };
                  })}
                  onChange={(value) => {
                    //Set Values of the form when organization is changed
                    const org = orgs.find((o) => o.id === value);
                    if (org && org.dataConfig) {
                      const config = org.dataConfig;
                      setSelectedOrg(org);
                      form.setFieldsValue({
                        googleAdsID: config.googleCustomerId ? config.googleCustomerId : null,
                        googleAnalyticsID:
                          config.googleAnalytics && config.googleAnalytics.viewId
                            ? config.googleAnalytics.viewId
                            : null,
                        googleAnalyticsViewIdIsGA4:
                          config.googleAnalytics && config.googleAnalytics.isGA4
                            ? config.googleAnalytics.isGA4
                            : null,
                        bingCustomerID:
                          config.bing && config.bing.customerId ? config.bing.customerId : null,
                        bingAccountID:
                          config.bing && config.bing.accountId
                            ? org.dataConfig.bing.accountId
                            : null,
                      });
                    }
                    fetchGoogleAdsCustomers({ variables: { orgId: value } });
                    fetchGoogleAnalyticsProperties({ variables: { orgId: value } });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Card title={'Channels'} bordered>
                <Row>
                  <Col span={24}>
                    <Text strong>Google</Text>
                  </Col>
                  <Col xs={6}>
                    <Form.Item
                      label="Process only with Google Ads"
                      name={'processWithGoogleAds'}
                      valuePropName="checked"
                    >
                      <Switch
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        onChange={(checked) => {
                          if (checked) {
                            //If checked disable google analytics
                            form.setFieldsValue({
                              googleAnalyticsID: null,
                              googleAnalyticsViewIdIsGA4: null,
                            });
                          }
                          setIsProcessWithGoogleAds(checked);
                          // Trigger validation for Google Analytics field to clear errors
                          form.validateFields(['googleAnalyticsID']);
                        }}
                      />
                    </Form.Item>
                    <Row>
                      <Col span={22}>
                        <Form.Item
                          label={
                            <Space>
                              Google Ads Customer ID
                              <Tooltip title="Kindly input your Customer ID without the dashes.">
                                <QuestionCircleOutlined />
                              </Tooltip>
                            </Space>
                          }
                          name={'googleAdsID'}
                          rules={[
                            () => ({
                              validator(_, value) {
                                //If process with google ads is enabled then google ads customer id is required
                                if (isProcessWithGoogleAds) {
                                  if (value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(`Please fill up Google Ads Customer ID`);
                                }
                                return Promise.resolve();
                              },
                            }),
                            {
                              pattern: /^[0-9]+$/,
                              message: 'Customer ID must contain only digits',
                            },
                          ]}
                        >
                          {googleAdsCustomers.length > 0 ? (
                            <Select
                              showSearch
                              placeholder="Select a Google Ads Customer"
                              loading={loadingGoogleAdsCustomers}
                              filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                              }
                              options={googleAdsCustomers.map((c) => ({
                                label: `${c.descriptiveName} (${c.customerId})`,
                                value: c.customerId,
                              }))}
                            />
                          ) : (
                            <Input placeholder="Customer ID" />
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="primary"
                          style={{ marginTop: '30px' }}
                          onClick={() => {
                            form.setFieldsValue({
                              googleAdsID: selectedOrg && selectedOrg.dataConfig ? selectedOrg.dataConfig.googleCustomerId : null,
                            });
                          }}
                        >
                          <UndoOutlined />
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={22}>
                        <Form.Item
                          label="Google Analytics View ID"
                          name={'googleAnalyticsID'}
                          rules={[
                            () => ({
                              validator(_, value) {
                                //If process with google ads is not enabled then google analytics view id is required
                                if (!isProcessWithGoogleAds) {
                                  if (value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(`Please fill up Google Analytics View ID`);
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          {googleAnalyticsProperties.length > 0 ? (
                            <Select
                              showSearch
                              placeholder="Select a Google Analytics Property"
                              loading={loadingGoogleAnalyticsProperties}
                              disabled={isProcessWithGoogleAds}
                              filterOption={function(input, option) {
                                return option.label.toLowerCase().includes(input.toLowerCase());
                              }}
                              options={googleAnalyticsProperties.map(function(p) {
                                return {
                                  label: '[' + p.type + '] ' + p.displayName,
                                  value: p.viewId,
                                  type: p.type,
                                };
                              })}
                              onChange={function(value, option) {
                                // Auto-set GA4 toggle based on selected property type
                                form.setFieldsValue({
                                  googleAnalyticsViewIdIsGA4: option.type === 'GA4',
                                });
                              }}
                            />
                          ) : (
                            <Input placeholder="Analytics ID" disabled={isProcessWithGoogleAds} />
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="primary"
                          style={{ marginTop: '30px' }}
                          onClick={() => {
                            var ga = selectedOrg && selectedOrg.dataConfig ? selectedOrg.dataConfig.googleAnalytics : null;
                            form.setFieldsValue({
                              googleAnalyticsID: ga ? ga.viewId : null,
                              googleAnalyticsViewIdIsGA4: ga ? ga.isGA4 : null,
                            });
                          }}
                        >
                          <UndoOutlined />
                        </Button>
                      </Col>
                    </Row>

                    <Space size="small" direction="horizontal">
                      <Typography.Text>UA</Typography.Text>
                      <Form.Item
                        name={'googleAnalyticsViewIdIsGA4'}
                        valuePropName="checked"
                        noStyle
                      >
                        <Switch disabled={isProcessWithGoogleAds} />
                      </Form.Item>
                      <Typography.Text>GA4</Typography.Text>
                    </Space>
                  </Col>
                  <Col xs={6} md={{ offset: 6 }}>
                    <Form.Item
                      label="Enable Revenue by Location"
                      name={'hasLocationRevenue'}
                      valuePropName="checked"
                    >
                      <Switch
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        onChange={(checked) => {
                          setIsRevenueByLocation(checked);
                        }}
                      />
                    </Form.Item>
                    <Form.Item label={'Locations'} name={'locationsKey'}>
                      <Select
                        mode="tags"
                        style={{ width: `100%` }}
                        allowClear={true}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        disabled={!isRevenueByLocation}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider />
                <Row>
                  <Col span={12}>
                    <Row>
                      <Col xs={24}>
                        <Text strong>Bing</Text>
                      </Col>
                      <Col xs={12}>
                        <Row>
                          <Col span={22}>
                            <Form.Item label="Customer ID" name={'bingCustomerID'}>
                              <Input placeholder="Customer ID" />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button
                              type="primary"
                              style={{ marginTop: '30px' }}
                              onClick={() => {
                                form.setFieldsValue({
                                  bingCustomerID:
                                    selectedOrg.dataConfig &&
                                    selectedOrg.dataConfig.bing &&
                                    selectedOrg.dataConfig.bing.customerId
                                      ? selectedOrg.dataConfig.bing.customerId
                                      : '',
                                });
                              }}
                            >
                              <UndoOutlined />
                            </Button>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={22}>
                            <Form.Item label="Account ID" name={'bingAccountID'}>
                              <Input placeholder="Account ID" />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button
                              type="primary"
                              style={{ marginTop: '30px' }}
                              onClick={() => {
                                form.setFieldsValue({
                                  bingAccountID:
                                    selectedOrg.dataConfig &&
                                    selectedOrg.dataConfig.bing &&
                                    selectedOrg.dataConfig.bing.accountId
                                      ? selectedOrg.dataConfig.bing.accountId
                                      : '',
                                });
                              }}
                            >
                              <UndoOutlined />
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row gutter={[24, 24]}>
                      <Col xs={24}>
                        <Text strong>Commerce</Text>
                      </Col>
                      <Col xs={12}>
                        <Form.Item
                          name={'hasCommerce'}
                          label={'Enable Commerce Tracking'}
                          valuePropName="checked"
                        >
                          <Switch
                            checkedChildren={<CheckOutlined />}
                            unCheckedChildren={<CloseOutlined />}
                            onChange={(checked) => {
                              if (!checked) {
                                form.setFieldsValue({
                                  commerceTags: null,
                                });
                              }
                              setIsCommerceEnabled(checked);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          name={'commerceTags'}
                          label={'Commerce Tag'}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator() {
                                if (getFieldValue('hasCommerce')) {
                                  if (getFieldValue('commerceTags')) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(`Please Select a Commerce Tag`);
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Select
                            style={{ width: '100%' }}
                            allowClear={true}
                            placeholder="Select Commerce Tag"
                            showSearch={true}
                            disabled={!isCommerceEnabled}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            filterOption={(input, option) => {
                              if (option.label.toLowerCase().includes(input.toLowerCase()))
                                return true;
                            }}
                            options={
                              tags &&
                              tags.map((tag) => {
                                return { value: tag.id, label: tag.name };
                              })
                            }
                            filterSort={(optionA, optionB) =>
                              optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Form.Item
                  name="hasChannels"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator() {
                        const hasGoogle =
                          getFieldValue('googleAdsID') || getFieldValue('googleAnalyticsID')
                            ? true
                            : false;
                        const hasBing =
                          getFieldValue('bingCustomerID') || getFieldValue('bingAccountID')
                            ? true
                            : false;
                        const hasCommerce =
                          getFieldValue('hasCommerce') && getFieldValue('commerceTags')
                            ? true
                            : false;

                        if (hasGoogle || hasBing || hasCommerce) {
                          console.log(`Resolved`);
                          return Promise.resolve();
                        }

                        return Promise.reject(`Please fill up at least one channel`);
                      },
                    }),
                  ]}
                >
                  <Input type="hidden" />
                </Form.Item>
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title={
                  <Row type="flex" gutter={10} align="middle">
                    <Col>
                      <RiseOutlined />
                      <span> Budget</span>
                    </Col>
                  </Row>
                }
              >
                <Row>
                  <Text type="secondary">
                    The amount you set as the overall budget will be the maximum amount that the
                    campaign will spend over the course of its flight dates.
                  </Text>
                </Row>
                <Row>
                  <Form.Item
                    name={'monthlyBudget'}
                    rules={[{ required: true, message: 'Budget Required' }]}
                  >
                    <InputNumber
                      addonBefore="$USD"
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                    />
                  </Form.Item>
                </Row>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default LaunchPaidSearch;
